import { useEffect, useState } from "react";
import api from "@/lib/api";
import { cad } from "@/lib/pricing";
import { Handshake, UserPlus, Copy, Check, MousePointerClick, DollarSign, TrendingUp, Percent, X, Send, Trash2, MailCheck } from "lucide-react";
import { toast } from "sonner";

export default function MerchantAffiliates() {
  const [data, setData] = useState({ affiliates: [], invitations: [], totals: {} });
  const [campaigns, setCampaigns] = useState([]);
  const [inviteEmail, setInviteEmail] = useState("");
  const [inviteNote, setInviteNote] = useState("");
  const [inviteBusy, setInviteBusy] = useState(false);
  const [resendingId, setResendingId] = useState(null);
  const [copiedToken, setCopiedToken] = useState(null);
  const [editing, setEditing] = useState(null); // campaign object under commission edit

  const load = async () => {
    try {
      const [{ data: aff }, { data: cmp }] = await Promise.all([
        api.get("/merchant/affiliates"),
        api.get("/merchant/campaigns"),
      ]);
      setData(aff);
      setCampaigns(cmp);
    } catch {
      // ignore
    }
  };

  useEffect(() => { load(); }, []);

  const invite = async (e) => {
    e.preventDefault();
    setInviteBusy(true);
    try {
      const res = await api.post("/merchant/affiliate-invitations", { email: inviteEmail, note: inviteNote || null });
      if (res.data?.email_sent) {
        toast.success(`Invitation email sent to ${inviteEmail}`);
      } else {
        toast.success("Invitation created");
      }
      setInviteEmail(""); setInviteNote("");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not invite");
    } finally { setInviteBusy(false); }
  };

  const resendInvite = async (inv) => {
    setResendingId(inv.id);
    try {
      const res = await api.post(`/merchant/affiliate-invitations/${inv.id}/resend`);
      if (res.data?.email_sent) {
        toast.success(`Invitation re-sent to ${inv.email}`);
      } else {
        toast.info("Invitation updated");
      }
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not resend email");
    } finally {
      setResendingId(null);
    }
  };

  const cancelInvite = async (inv) => {
    try {
      await api.delete(`/merchant/affiliate-invitations/${inv.id}`);
      toast.success("Invitation removed");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not remove invitation");
    }
  };

  const copyInvite = (inv) => {
    const link = `${window.location.origin}/affiliate/apply?invite=${inv.token}`;
    navigator.clipboard.writeText(link);
    setCopiedToken(inv.token);
    toast.success("Invite link copied");
    setTimeout(() => setCopiedToken(null), 1500);
  };

  const saveCommission = async (payload) => {
    try {
      await api.put(`/merchant/campaigns/${editing.id}/commission`, payload);
      toast.success("Commission updated");
      setEditing(null);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed");
    }
  };

  return (
    <div data-testid="merchant-affiliates">
      <div className="pf-chip pf-chip-lav"><Handshake className="w-3 h-3" /> Affiliates</div>
      <h2 className="font-display text-3xl md:text-4xl font-medium tracking-tight mt-3 text-slate-900">Affiliate program</h2>
      <p className="text-sm text-slate-600 mt-1 max-w-2xl">
        Invite creators, bloggers and Cyannect shoppers to promote your campaigns. Set per-campaign commissions — pay only on completed orders.
      </p>

      <div className="grid md:grid-cols-3 gap-3 mt-6">
        <div className="pf-card p-5">
          <div className="text-xs uppercase tracking-widest text-slate-500 font-semibold flex items-center gap-1"><MousePointerClick className="w-3 h-3" /> Total clicks</div>
          <div className="font-display text-3xl mt-2 tabular-nums" data-testid="aff-total-clicks">{data.totals.clicks || 0}</div>
        </div>
        <div className="pf-card p-5">
          <div className="text-xs uppercase tracking-widest text-slate-500 font-semibold flex items-center gap-1"><TrendingUp className="w-3 h-3" /> Conversions</div>
          <div className="font-display text-3xl mt-2 tabular-nums" data-testid="aff-total-conversions">{data.totals.conversions || 0}</div>
        </div>
        <div className="pf-card p-5">
          <div className="text-xs uppercase tracking-widest text-slate-500 font-semibold flex items-center gap-1"><DollarSign className="w-3 h-3" /> Commissions accrued</div>
          <div className="font-display text-3xl mt-2 tabular-nums" data-testid="aff-total-commissions">{cad((data.totals.commissions || 0))}</div>
        </div>
      </div>

      {/* Campaign commission table */}
      <div className="pf-card p-6 mt-6 overflow-x-auto">
        <div className="flex items-center justify-between mb-3">
          <div>
            <div className="text-xs uppercase tracking-widest text-slate-500 font-semibold flex items-center gap-1"><Percent className="w-3 h-3" /> Per-campaign commissions</div>
            <div className="text-sm text-slate-600">Enable and configure a payout for each promotable campaign.</div>
          </div>
        </div>
        <table className="w-full text-sm">
          <thead className="bg-white/40 text-left text-xs uppercase tracking-widest text-slate-500">
            <tr>
              <th className="px-3 py-2">Campaign</th>
              <th className="px-3 py-2">Affiliate</th>
              <th className="px-3 py-2">Model</th>
              <th className="px-3 py-2">Rate</th>
              <th className="px-3 py-2 text-right"></th>
            </tr>
          </thead>
          <tbody>
            {campaigns.map((c) => (
              <tr key={c.id} className="border-t border-white/60" data-testid={`campaign-comm-row-${c.id}`}>
                <td className="px-3 py-2 font-medium">{c.name}</td>
                <td className="px-3 py-2"><span className={`pf-chip ${c.affiliate_enabled ? "pf-chip-mint" : ""}`}>{c.affiliate_enabled ? "enabled" : "off"}</span></td>
                <td className="px-3 py-2 text-xs">{c.commission_type || "—"}</td>
                <td className="px-3 py-2 text-xs">
                  {c.commission_type === "percent" && `${c.commission_value}%`}
                  {c.commission_type === "flat" && `CA$${c.commission_value}`}
                  {c.commission_type === "tiered" && "Tiered"}
                  {!c.commission_type && "—"}
                </td>
                <td className="px-3 py-2 text-right">
                  <button onClick={() => setEditing(c)} className="pf-btn-glass px-3 py-1 rounded-full text-xs font-semibold" data-testid={`edit-comm-${c.id}`}>Edit</button>
                </td>
              </tr>
            ))}
            {campaigns.length === 0 && <tr><td colSpan={5} className="px-3 py-6 text-center text-slate-500">Create a campaign first.</td></tr>}
          </tbody>
        </table>
      </div>

      {/* Invite form */}
      <div className="pf-card p-6 mt-6">
        <div className="text-xs uppercase tracking-widest text-slate-500 font-semibold flex items-center gap-1"><UserPlus className="w-3 h-3" /> Invite an affiliate</div>
        <form onSubmit={invite} className="mt-3 flex flex-col md:flex-row gap-3" data-testid="invite-form">
          <input required type="email" value={inviteEmail} onChange={(e) => setInviteEmail(e.target.value)} placeholder="creator@example.com" className="pf-input flex-1 px-3 py-2 text-sm" data-testid="invite-email" />
          <input type="text" value={inviteNote} onChange={(e) => setInviteNote(e.target.value)} placeholder="Note (optional)" className="pf-input flex-1 px-3 py-2 text-sm" data-testid="invite-note" />
          <button type="submit" disabled={inviteBusy} className="pf-btn-primary px-4 py-2 rounded-full text-sm font-semibold" data-testid="invite-submit">
            {inviteBusy ? "Sending…" : "Send invite"}
          </button>
        </form>
        {data.invitations.length > 0 && (
          <div className="mt-4">
            <div className="text-xs uppercase tracking-widest text-slate-500 font-semibold mb-2">Pending invitations</div>
            <div className="space-y-2">
              {data.invitations.filter((i) => i.status === "pending").map((inv) => (
                <div key={inv.id} className="flex items-center justify-between gap-3 text-sm border border-white/60 rounded-xl px-3 py-2 bg-white/40" data-testid={`inv-row-${inv.id}`}>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="font-medium">{inv.email}</span>
                      <span className="text-[10px] pf-chip pf-chip-lav py-0.5">Invited</span>
                    </div>
                    {inv.note && <div className="text-slate-500 text-xs truncate mt-0.5">&ldquo;{inv.note}&rdquo;</div>}
                  </div>
                  <div className="flex items-center gap-1.5 shrink-0">
                    <button
                      type="button"
                      onClick={() => resendInvite(inv)}
                      disabled={resendingId === inv.id}
                      className="pf-btn-glass px-2.5 py-1 rounded-full text-xs font-semibold flex items-center gap-1 disabled:opacity-50"
                      title="Resend invitation email"
                      data-testid={`resend-invite-${inv.id}`}
                    >
                      <Send className="w-3 h-3 text-cyan-600" />
                      {resendingId === inv.id ? "Sending…" : "Resend email"}
                    </button>
                    <button
                      type="button"
                      onClick={() => copyInvite(inv)}
                      className="pf-btn-glass px-2.5 py-1 rounded-full text-xs font-semibold flex items-center gap-1"
                      data-testid={`copy-invite-${inv.id}`}
                    >
                      {copiedToken === inv.token ? <Check className="w-3 h-3 text-emerald-600" /> : <Copy className="w-3 h-3" />}
                      {copiedToken === inv.token ? "Copied" : "Copy link"}
                    </button>
                    <button
                      type="button"
                      onClick={() => cancelInvite(inv)}
                      className="p-1 rounded-full text-slate-400 hover:text-red-600 transition"
                      title="Cancel invitation"
                      data-testid={`cancel-invite-${inv.id}`}
                    >
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Affiliates table */}
      <div className="pf-card overflow-x-auto mt-6">
        <div className="p-4 text-xs uppercase tracking-widest text-slate-500 font-semibold">Your affiliates</div>
        <table className="w-full text-sm">
          <thead className="bg-white/40 text-left text-xs uppercase tracking-widest text-slate-500">
            <tr>
              <th className="px-5 py-2">Affiliate</th>
              <th className="px-5 py-2">Clicks</th>
              <th className="px-5 py-2">Conversions</th>
              <th className="px-5 py-2">Revenue</th>
              <th className="px-5 py-2">Commission owed</th>
            </tr>
          </thead>
          <tbody>
            {data.affiliates.map((a) => (
              <tr key={a.affiliate_id} className="border-t border-white/60" data-testid={`merch-aff-row-${a.affiliate_id}`}>
                <td className="px-5 py-2">
                  <div className="font-medium">{a.affiliate_name || a.affiliate_slug}</div>
                  <div className="text-xs text-slate-500 font-mono">{a.affiliate_slug}</div>
                </td>
                <td className="px-5 py-2 tabular-nums">{a.clicks}</td>
                <td className="px-5 py-2 tabular-nums">{a.conversions}</td>
                <td className="px-5 py-2 tabular-nums">{cad(a.revenue_total)}</td>
                <td className="px-5 py-2 tabular-nums font-semibold text-cyan-700">{cad(a.commission_total)}</td>
              </tr>
            ))}
            {data.affiliates.length === 0 && <tr><td colSpan={5} className="px-5 py-8 text-center text-slate-500">No affiliates yet — invite one above.</td></tr>}
          </tbody>
        </table>
      </div>

      {editing && <CommissionEditor campaign={editing} onClose={() => setEditing(null)} onSave={saveCommission} />}
    </div>
  );
}

function CommissionEditor({ campaign, onClose, onSave }) {
  const [enabled, setEnabled] = useState(!!campaign.affiliate_enabled);
  const [type, setType] = useState(campaign.commission_type || "percent");
  const [value, setValue] = useState(
    Array.isArray(campaign.commission_value) ? "" : (campaign.commission_value || 0)
  );
  const [tiers, setTiers] = useState(
    Array.isArray(campaign.commission_value) && campaign.commission_value.length
      ? campaign.commission_value
      : [{ min: 0, max: 1000, pct: 5 }, { min: 1000, max: null, pct: 8 }]
  );

  const submit = (e) => {
    e.preventDefault();
    const payload = { affiliate_enabled: enabled, commission_type: type };
    payload.commission_value = type === "tiered" ? tiers : Number(value);
    onSave(payload);
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="commission-editor">
      <div className="absolute inset-0 bg-slate-900/50 backdrop-blur-md" onClick={onClose} />
      <form onSubmit={submit} className="glass-strong rounded-[28px] max-w-lg w-full p-8 relative z-10 fade-up space-y-5">
        <button type="button" onClick={onClose} className="absolute top-4 right-4 p-1.5 rounded-full hover:bg-white/70 text-slate-500" aria-label="Close">
          <X className="w-4 h-4" />
        </button>
        <div>
          <div className="pf-chip pf-chip-lav">Campaign</div>
          <h3 className="font-display text-2xl mt-2 text-slate-900">{campaign.name}</h3>
        </div>

        <label className="flex items-center gap-3 cursor-pointer">
          <input type="checkbox" checked={enabled} onChange={(e) => setEnabled(e.target.checked)} data-testid="comm-enabled" />
          <span className="text-sm font-medium">Enable affiliate program for this campaign</span>
        </label>

        <div>
          <label className="block text-xs font-semibold uppercase tracking-widest text-slate-500 mb-2">Commission model</label>
          <div className="flex gap-2">
            {["percent", "flat", "tiered"].map((t) => (
              <button type="button" key={t} onClick={() => setType(t)} className={`pf-chip cursor-pointer ${type === t ? "pf-chip-mint" : ""}`} data-testid={`comm-type-${t}`}>
                {t === "percent" ? "% of revenue" : t === "flat" ? "$ flat" : "Tiered"}
              </button>
            ))}
          </div>
        </div>

        {type !== "tiered" ? (
          <div>
            <label className="block text-xs font-semibold uppercase tracking-widest text-slate-500 mb-2">
              {type === "percent" ? "Percentage (%)" : "Flat amount ($)"}
            </label>
            <input required type="number" step="0.01" min="0" value={value} onChange={(e) => setValue(e.target.value)} className="pf-input w-full px-3 py-2 text-sm" data-testid="comm-value" />
          </div>
        ) : (
          <div>
            <label className="block text-xs font-semibold uppercase tracking-widest text-slate-500 mb-2">Tiers</label>
            <div className="space-y-2">
              {tiers.map((t, i) => (
                <div key={i} className="grid grid-cols-4 gap-2 items-center">
                  <input type="number" placeholder="Min $" value={t.min} onChange={(e) => setTiers(tiers.map((x, ii) => ii === i ? { ...x, min: Number(e.target.value) } : x))} className="pf-input px-2 py-1.5 text-xs" />
                  <input type="number" placeholder="Max $ (blank = ∞)" value={t.max ?? ""} onChange={(e) => setTiers(tiers.map((x, ii) => ii === i ? { ...x, max: e.target.value === "" ? null : Number(e.target.value) } : x))} className="pf-input px-2 py-1.5 text-xs" />
                  <input type="number" step="0.01" placeholder="% pct" value={t.pct} onChange={(e) => setTiers(tiers.map((x, ii) => ii === i ? { ...x, pct: Number(e.target.value) } : x))} className="pf-input px-2 py-1.5 text-xs" />
                  <button type="button" onClick={() => setTiers(tiers.filter((_, ii) => ii !== i))} className="text-xs text-red-600 hover:underline">Remove</button>
                </div>
              ))}
              <button type="button" onClick={() => setTiers([...tiers, { min: 0, max: null, pct: 0 }])} className="pf-btn-glass px-3 py-1.5 rounded-full text-xs font-semibold">+ Add tier</button>
            </div>
          </div>
        )}

        <div className="flex gap-2 justify-end pt-2">
          <button type="button" onClick={onClose} className="pf-btn-glass px-4 py-2 rounded-full text-sm font-semibold">Cancel</button>
          <button type="submit" className="pf-btn-primary px-4 py-2 rounded-full text-sm font-semibold" data-testid="comm-save">Save</button>
        </div>
      </form>
    </div>
  );
}
